import React, { useState } from 'react';
import { Target, TrendingUp, Star, Filter } from 'lucide-react';
import { useApp } from '../context/AppContext';
import Badge from '../components/Badge';
import { ProgressBar, PieChart } from '../components/Charts';
import Breadcrumb from '../components/Breadcrumb';
import { Link } from 'react-router-dom';
import { formatDate, getLeadScore, groupByField } from '../utils/helpers';

export default function LeadManagement() {
  const { state, dispatch, showNotification } = useApp();
  const [filterStatus, setFilterStatus] = useState('');

  const filtered = filterStatus ? state.prospects.filter(p => p.status === filterStatus) : state.prospects;
  const sorted = [...filtered].sort((a, b) => getLeadScore(b) - getLeadScore(a));

  const grouped = groupByField(state.prospects, 'status');
  const bySource = groupByField(state.prospects, 'sumberLead');

  const statusPieData = [
    { label: 'Hot', value: (grouped.Hot || []).length, color: '#DC2626' },
    { label: 'Warm', value: (grouped.Warm || []).length, color: '#EA580C' },
    { label: 'Cold', value: (grouped.Cold || []).length, color: '#3B82F6' },
  ];

  const sourcePieData = Object.entries(bySource).map(([key, arr], i) => ({
    label: key,
    value: arr.length,
    color: ['#DC2626', '#EA580C', '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B'][i % 6],
  }));

  const updateStatus = (prospect, status) => {
    dispatch({ type: 'UPDATE_PROSPECT', payload: { ...prospect, status } });
    showNotification(`Status ${prospect.nama} diperbarui ke ${status}`);
  };

  const scoreColor = (score) => score >= 70 ? 'text-green-600' : score >= 40 ? 'text-orange-500' : 'text-red-500';
  const scoreBar = (score) => score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-orange-500' : 'bg-red-500';

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Lead Management' }]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Lead Management</h1>
          <p className="text-sm text-gray-500">Kelola dan prioritaskan prospek berdasarkan lead score</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Distribusi Status Lead</h3>
          <PieChart data={statusPieData} />
        </div>
        <div className="card">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Distribusi Sumber Lead</h3>
          <PieChart data={sourcePieData} />
        </div>
      </div>

      {/* Conversion funnel */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Funnel Konversi</h3>
        <div className="space-y-3">
          {[
            { label: 'Total Prospek', value: state.prospects.length, max: state.prospects.length, color: 'bg-blue-500' },
            { label: 'Hot Leads', value: (grouped.Hot || []).length, max: state.prospects.length, color: 'bg-red-500' },
            { label: 'SPK Dibuat', value: state.spk.length, max: state.prospects.length, color: 'bg-orange-500' },
            { label: 'SPK Selesai', value: state.spk.filter(s => s.status === 'Selesai').length, max: state.prospects.length, color: 'bg-green-500' },
            { label: 'Terkirim', value: state.delivery.filter(d => d.statusPengiriman === 'Terkirim').length, max: state.prospects.length, color: 'bg-teal-500' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4">
              <span className="text-xs text-gray-600 w-32">{item.label}</span>
              <div className="flex-1">
                <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-4 rounded-full transition-all duration-700 ${item.color}`} style={{ width: `${Math.min((item.value / item.max) * 100, 100)}%` }} />
                </div>
              </div>
              <span className="text-sm font-semibold w-8 text-right">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Lead Scoring Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">Prioritas Lead (berdasarkan score)</h3>
          <div className="flex gap-2">
            {['', 'Hot', 'Warm', 'Cold'].map(s => (
              <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${filterStatus === s ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {s || 'Semua'}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {sorted.map((p, index) => {
            const score = getLeadScore(p);
            return (
              <div key={p.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center text-xs font-bold text-gray-500 border border-gray-200 flex-shrink-0">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Link to={`/prospecting/${p.id}`} className="text-sm font-medium text-gray-800 hover:text-red-600 transition-colors">{p.nama}</Link>
                    <Badge status={p.status} />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 max-w-32">
                      <div className="h-1.5 bg-gray-200 rounded-full">
                        <div className={`h-1.5 rounded-full ${scoreBar(score)}`} style={{ width: `${score}%` }} />
                      </div>
                    </div>
                    <span className={`text-xs font-bold ${scoreColor(score)}`}>Score: {score}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{p.mobilDiminati}</p>
                  <p className="text-xs text-gray-400">{p.sumberLead}</p>
                </div>
                <div className="flex gap-1">
                  {['Hot', 'Warm', 'Cold'].filter(s => s !== p.status).map(s => (
                    <button key={s} onClick={() => updateStatus(p, s)} className="px-2 py-1 text-xs rounded-lg bg-white border border-gray-200 hover:bg-gray-50 text-gray-600 transition-colors">
                      → {s}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {sorted.length === 0 && <p className="text-center text-gray-400 py-8">Tidak ada lead ditemukan</p>}
        </div>
      </div>
    </div>
  );
}
